import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CollapsibleSectionProps {
  title: string;
  count: number;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children?: React.ReactNode;
  className?: string;
  muted?: boolean;
  /** Maximum items to show before "See more" button appears */
  maxVisible?: number;
  /** Render function for items - use this when you need "See more" functionality */
  items?: React.ReactNode[];
}

export const CollapsibleSection = ({
  title,
  count,
  icon,
  defaultOpen = false,
  children,
  className,
  muted = false,
  maxVisible = 5,
  items,
}: CollapsibleSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);

  // If items array is provided, use it for "See more" functionality
  const hasItems = items && items.length > 0;
  const visibleItems = hasItems
    ? showAll
      ? items
      : items.slice(0, maxVisible)
    : null;
  const hasMoreItems = hasItems && items.length > maxVisible;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <CollapsibleTrigger className="w-full">
        <div
          className={cn(
            "flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer",
            "hover:bg-muted/50",
            muted ? "text-muted-foreground" : "text-foreground"
          )}
        >
          <div className="flex items-center gap-2">
            {icon}
            <h2 className={cn("text-lg font-semibold", muted && "text-muted-foreground")}>
              {title}
            </h2>
            <span
              className={cn(
                "text-sm px-2 py-0.5 rounded-full",
                muted
                  ? "bg-muted text-muted-foreground"
                  : "bg-primary/10 text-primary"
              )}
            >
              {count}
            </span>
          </div>
          {isOpen ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-4 pt-2">
          {hasItems ? (
            <>
              {visibleItems}
              {hasMoreItems && (
                <Button
                  variant="ghost"
                  className="w-full text-muted-foreground hover:text-foreground"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAll(!showAll);
                  }}
                >
                  {showAll ? "Show less" : `See more (${items.length - maxVisible} more)`}
                </Button>
              )}
            </>
          ) : (
            children
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
